import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";

const statusStyles = {
  Active: { bgcolor: "#8CD47E", color: "#fff", borderColor: "#8CD47E" },
  Pending: { bgcolor: "#F8D66D", color: "#fff", borderColor: "#F8D66D" },
  Rejected: { bgcolor: "#FF6961", color: "#fff", borderColor: "#FF6961" },
};

// Helper function to get possible status transitions
const getPossibleTransitions = (currentStatus) => {
  const transitions = {
    Pending: ["Active", "Rejected"],
    Active: ["Rejected"],
    Rejected: ["Active"]
  };
  return transitions[currentStatus] || [];
};

const calculateRemainingCredits = (client) => {

  const used = client?.credits?.usedCredits || 0;
  const remaining =client?.credits?.totalCredits || 0;
    const total = used+remaining;
  
  const hasCredits = client?.credits && (total > 0 || used > 0);
  
  if (!hasCredits) {
    return {
      remaining: 0,
      total: 0,
      used: 0,
      display: "-"
    };
  }
  
  return {
    remaining: remaining,
    total: total,
    used: used,
    display: `${remaining}/${total}`
  };
};

const ClientStatusUpdateModal = ({
  open,
  onClose,
  client,
  fromStatus = "Pending",
  toStatus = "Active",
  onUpdate, // Changed from onApprove/onReject
  hideCredits = false,
}) => {
  // Get possible status transitions
  const possibleTransitions = useMemo(() => 
    getPossibleTransitions(fromStatus), [fromStatus]
  );
  
  // Initialize selectedStatus
  const [selectedStatus, setSelectedStatus] = useState(null);

  // Set selection when modal opens
  useEffect(() => {
    if (open) {
      if (fromStatus === "Pending") {
        setSelectedStatus("Active");
      } else {
        setSelectedStatus(null);
      }
    }
  }, [open, fromStatus]);
  
  // Calculate credits info
  const creditsInfo = useMemo(() => 
    calculateRemainingCredits(client), [client]
  );

  // Handle status button click
  const handleStatusClick = (status) => {
    // Toggle: if clicking the already selected status, deselect it
    if (selectedStatus === status) {
      setSelectedStatus(null); // Deselect
    } else {
      setSelectedStatus(status); // Select new status
    }
  };

  // Handle update with selected status
  const handleUpdate = () => {
    if (onUpdate && selectedStatus) {
      onUpdate(selectedStatus);
    }
  };

  // Early return after all hooks
  if (!open || !client) return null;

  return (
    <>
      {/* Overlay */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1400,
        }}
      />

      {/* Modal */}
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 700,
          bgcolor: "#fff",
          borderRadius: 2,
          p: 3,
          zIndex: 1401,
          boxShadow: "0px 12px 30px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
            Client Status Update
          </Typography>

          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <Icon name="close" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Status Change Section */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1.5 }}>
            Current status: <strong>{fromStatus}</strong>
          </Typography>
          
          <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY, mb: 1.5 }}>
            Change to:
          </Typography>
          
          <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
            {possibleTransitions.map((status) => {
              const isSelected = selectedStatus === status;
              const statusStyle = statusStyles[status];
              
              return (
                <Button
                  key={status}
                  variant={isSelected ? "contained" : "outlined"}
                  onClick={() => handleStatusClick(status)}
                  sx={{
                    textTransform: "none",
                    minWidth: 100,
                    borderColor: statusStyle?.borderColor || COLORS.PRIMARY,
                    bgcolor: isSelected ? statusStyle?.bgcolor : "transparent",
                    color: isSelected ? "#fff" : statusStyle?.borderColor,
                    '&:hover': {
                      bgcolor: isSelected 
                        ? statusStyle?.bgcolor 
                        : statusStyle?.borderColor + "10",
                      borderColor: statusStyle?.borderColor,
                      color: isSelected ? "#fff" : statusStyle?.borderColor,
                    }
                  }}
                >
                  {status}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* Client Info */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            {/* LEFT COLUMN */}
            <Box>
              {/* Name + Status */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon name="profile" color={COLORS.PRIMARY} />
                <Typography sx={{...TYPOGRAPHY.H3 ,color: COLORS.TEXT}}>
                  {client.name}
                </Typography>

                <Chip
                  label={fromStatus}
                  size="small"
                  sx={{ ...statusStyles[fromStatus], height: 20,...TYPOGRAPHY.BODY_SMALL }}
                />

                <Icon
                  name="arrowRight"
                  size={16}
                  stroke={2}
                  color={COLORS.PRIMARY}
                  sx={{
                    mx: 1.5,
                    transform: "scaleX(10)",
                  }}
                />
                
                {selectedStatus ? (
                  <Chip
                    label={selectedStatus}
                    size="small"
                    sx={{ ...statusStyles[selectedStatus], height: 20,...TYPOGRAPHY.BODY_SMALL  }}
                  />
                ) : (
                  <Chip
                    label="Not selected"
                    size="small"
                    sx={{ 
                      bgcolor: "#f0f0f0", 
                      color: "#888", 
                      height: 20,
                      ...TYPOGRAPHY.BODY_SMALL  
                    }}
                  />
                )}
              </Box>

              {/* Email */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1,pl:"24px" }}>
                <Icon name="at" size={14} color={COLORS.SECONDARY} />
                <Typography sx={{...TYPOGRAPHY.BODY_STANDARD,color: COLORS.SECONDARY}}>
                  {client.email}
                </Typography>
              </Box>

              {/* Phone */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 ,pl:"24px"}}>
                <Icon name="phoneDone" size={14} color={COLORS.SECONDARY} />
                <Typography sx={{...TYPOGRAPHY.BODY_STANDARD,color: COLORS.SECONDARY}}>
                  {client.phone}
                </Typography>
              </Box>
            </Box>

            {/* RIGHT COLUMN (ROW ALIGNED) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              {/* Row 1 — aligns with Name */}
              <Typography sx={{...TYPOGRAPHY.BODY_LARGE,color: COLORS.TEXT}}>
                Joined on:{" "}
                {new Date(
                  client.joinedAt || client.createdAt
                ).toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/')}
              </Typography>

              {/* Row 2 — spacer for Email */}
              <Box sx={{ height: 30 }} />

              {/* Row 3 — aligns with Phone */}
              {!hideCredits && (
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    color: COLORS.SECONDARY,
                    whiteSpace: "nowrap",
                  }}
                >
                  Credits: {creditsInfo.display}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`, my: 2 }} />

        {/* Confirmation Text */}
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, mb: 3 , color: COLORS.TEXT}}>
          {selectedStatus ? (
            <>
              Click "Update Status" to change status from{" "}
              <strong>"{fromStatus}"</strong> to{" "}
              <strong>"{selectedStatus}"</strong> for{" "}
              {client.name}
            </>
          ) : (
            <>
              Please select a status to update from{" "}
              <strong>"{fromStatus}"</strong> for {client.name}.
            </>
          )}
        </Typography>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Icon name="close2" />}
            onClick={onClose}
            sx={{ 
              textTransform: "none",
              borderColor: COLORS.ERROR,
              color: COLORS.ERROR
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={<Icon name="check" />}
            sx={{ 
              bgcolor: COLORS.PRIMARY, 
              textTransform: "none",
              opacity: selectedStatus ? 1 : 0.5,
              '&:disabled': {
                bgcolor: COLORS.PRIMARY,
                color: "#fff",
              }
            }}
            onClick={handleUpdate}
            disabled={!selectedStatus}
          >
            Update Status
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ClientStatusUpdateModal;